import styled from "styled-components";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import ChevronLeftIcon from "../../icons/chevron-left.svg?react";
import ChevronsLeftIcon from "../../icons/chevrons-left.svg?react";
import ChevronRightIcon from "../../icons/chevron-right.svg?react";
import ChevronsRightIcon from "../../icons/chevrons-right.svg?react";
import {colorPalette} from "../../styles/colorPalette";
import {NumberInputV5, pageNumberClassName} from "../../details/datatypes/numeric/NumberInputV5";
import {baseDistance, FlexCenterStyle, fontSize, fontWeight, radius} from "../../styles/styles";
import {HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {MathUtils} from "../../../../utils/math/MathUtils";

interface ITableFooterProps {
	readonly startIndex: number;
	readonly lastIndex: number;
	readonly totalCount: number;
	readonly itemsPerPage: number;
	readonly currentPage: number;
	readonly onPageChange: (index: number) => void;
}

export const TableFooterV5 = (props: ITableFooterProps) => {
	const {startIndex, lastIndex, totalCount, itemsPerPage, currentPage, onPageChange} = props;

	const getErrorMessage = (value: string) => {
		const isPositiveInteger = MathUtils.isPositiveInteger(value);
		if (isPositiveInteger) {
			const valueAsNumber = parseInt(value);
			if (valueAsNumber <= numberOfPages) {
				return "";
			}
		}

		return "Please provide a valid page number!";
	};

	const onChangePage = (value: number) => {
		const val = Math.round(value ?? currentPage + 1);
		const clampedValue = MathUtils.clamp(val, 1, numberOfPages);

		if (!isNaN(clampedValue) && clampedValue - 1 !== currentPage) {
			onPageChange(clampedValue - 1);
		}
	};

	const numberOfPages = Math.ceil(totalCount / itemsPerPage);
	const firstPageIndex = 0;
	const lastPageIndex = numberOfPages - (numberOfPages === 0 ? 0 : 1);

	return (
		<TableFooterStyled
			className="TableFooter"
			data-cy="table-footer"
		>
			<span>
				{`${totalCount === 0 ? 0 : startIndex + 1}-${totalCount === 0 ? 0 : lastIndex + 1}`}&nbsp;of&nbsp;
				<span data-cy="total-table-row-count">{`${totalCount}`}</span>&nbsp;items
			</span>
			<div className="pagination">
				<span className="pages">
					Page&nbsp;{currentPage + 1}&nbsp;of&nbsp;<span data-cy="total-page-count">{numberOfPages}</span>
				</span>
				{currentPage > firstPageIndex && (
					<IconButtonV5
						IconComponent={ChevronsLeftIcon}
						onClick={() => onPageChange(firstPageIndex)}
					/>
				)}
				{currentPage > firstPageIndex && (
					<IconButtonV5
						IconComponent={ChevronLeftIcon}
						onClick={() => onPageChange(props.currentPage - 1)}
					/>
				)}
				<NumberInputV5
					className={pageNumberClassName}
					decimals={0}
					value={currentPage + 1}
					onChange={(value) => onChangePage(value)}
					autoFocus={false}
					onlyNaturalNumbers={true}
					getErrorMessage={getErrorMessage}
					errorMessageHorizontalAlignment={HorizontalAlignment.center}
					errorMessageVerticalAlignment={VerticalAlignment.topOuter}
				/>
				{currentPage < lastPageIndex && (
					<IconButtonV5
						IconComponent={ChevronRightIcon}
						onClick={() => onPageChange(props.currentPage + 1)}
					/>
				)}
				{currentPage < lastPageIndex && (
					<IconButtonV5
						IconComponent={ChevronsRightIcon}
						onClick={() => onPageChange(lastPageIndex)}
					/>
				)}
			</div>
		</TableFooterStyled>
	);
};

const TableFooterStyled = styled.div`
	${FlexCenterStyle}
	justify-content: space-between;
	color: ${colorPalette.gray.c700Dark};
	position: relative;

	.infoIcon {
		position: initial !important;
	}

	.pagination {
		display: flex;
		align-items: center;

		.pages {
			margin: 0 ${baseDistance.sm};
		}

		.IconButton {
			margin: 0 ${baseDistance.xs};
		}

		.${pageNumberClassName} {
			${FlexCenterStyle}
			margin: 0 ${baseDistance.xs};
			width: 24px;
			height: 24px;
			border: 1px solid ${colorPalette.blueGray.c500Primary};
			border-radius: ${radius.sm};
			font-size: ${fontSize.sm};
			font-weight: ${fontWeight.bold};
			color: ${colorPalette.gray.c950};
			outline: none;
			justify-content: center;
			cursor: text;
			text-align: center;

			&:hover {
				color: ${colorPalette.gray.c950};
				background-color: ${colorPalette.gray.c200Light};
			}
			&:focus {
				border-color: ${colorPalette.primary.c500Primary};
				background-color: ${colorPalette.white};
			}
		}
	}
`;
